import { useState, useEffect, useRef } from "react";

const SLIDES = [
  {
    id: 1,
    tag: "Resume Matching",
    title: "Specialized job-matching algorithms",
    desc: "VybogTAL ranks your resume against live job postings and surfaces the best-fit roles in seconds.",
    icon: (
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
        <rect width="40" height="40" rx="12" fill="rgba(255,255,255,0.12)" />
        <path d="M12 20h16M20 12l8 8-8 8" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    preview: (
      <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 10 }}>
        {[["Senior Frontend Engineer", "94%", "#a78bfa"], ["Product Designer", "81%", "#818cf8"], ["UX Researcher", "67%", "#6366f1"]].map(([role, pct, color], i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "rgba(255,255,255,0.06)", borderRadius: 10, padding: "10px 14px", border: "1px solid rgba(255,255,255,0.08)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: color }} />
              <span style={{ fontSize: 13, color: "rgba(255,255,255,0.85)", fontWeight: 500 }}>{role}</span>
            </div>
            <span style={{ fontSize: 12, fontWeight: 700, color, background: `${color}22`, padding: "3px 9px", borderRadius: 20 }}>{pct}</span>
          </div>
        ))}
      </div>
    ),
  },
  {
    id: 2,
    tag: "Hiring Analytics",
    title: "Track hiring performance",
    desc: "Monitor your recruitment pipeline. Analyze applicants, interviews, and hiring trends for better decisions.",
    icon: (
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
        <rect width="40" height="40" rx="12" fill="rgba(255,255,255,0.12)" />
        <rect x="11" y="24" width="4" height="8" rx="1.5" fill="rgba(255,255,255,0.5)" />
        <rect x="18" y="18" width="4" height="14" rx="1.5" fill="rgba(255,255,255,0.8)" />
        <rect x="25" y="12" width="4" height="20" rx="1.5" fill="#fff" />
      </svg>
    ),
    preview: (
      <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
          {[["324", "Applicants"], ["86", "Interviews"], ["12", "Hired"]].map(([val, lbl]) => (
            <div key={lbl} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#fff" }}>{val}</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", marginTop: 2 }}>{lbl}</div>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", alignItems: "flex-end", gap: 5, height: 48 }}>
          {[40, 60, 50, 70, 55, 82, 68, 76, 58, 90, 65, 74].map((h, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                height: `${h}%`,
                borderRadius: "3px 3px 1px 1px",
                background: i === 9 ? "#a78bfa" : "rgba(255,255,255,0.22)"
              }}
            />
          ))}
        </div>
      </div>
    ),
  },
  {
    id: 3,
    tag: "Candidate Hub",
    title: "One place for every profile",
    desc: "Manage candidates, track interview stages, and collaborate with your team.",
    icon: (
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
        <rect width="40" height="40" rx="12" fill="rgba(255,255,255,0.12)" />
        <circle cx="20" cy="16" r="5" stroke="#fff" strokeWidth="2" />
        <path d="M10 30c0-5.523 4.477-10 10-10s10 4.477 10 10" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
    preview: (
      <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 9 }}>
        {[["Sarah K.", "Frontend Engineer", "Interview", 240], ["James L.", "Product Lead", "Review", 200], ["Maya P.", "Designer", "Shortlisted", 270]].map(([name, role, status, hue], i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "rgba(255,255,255,0.06)", borderRadius: 10, padding: "9px 12px", border: "1px solid rgba(255,255,255,0.08)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 28, height: 28, borderRadius: "50%", background: `hsl(${hue},55%,62%)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff" }}>{name[0]}</div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>{name}</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)" }}>{role}</div>
              </div>
            </div>
            <span style={{ fontSize: 10, fontWeight: 600, color: "rgba(255,255,255,0.65)", background: "rgba(255,255,255,0.09)", padding: "3px 8px", borderRadius: 20, border: "1px solid rgba(255,255,255,0.12)" }}>{status}</span>
          </div>
        ))}
      </div>
    ),
  },
];

export default function StackedCardCarousel() {
  const [active, setActive] = useState(0);
  const [visible, setVisible] = useState(true);
  const [cardHeight, setCardHeight] = useState(380);
  const rootRef = useRef(null);
  const total = SLIDES.length;

  // Dynamically scale card height to fit the available container height
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const compute = () => {
      const available = el.clientHeight;
      // Reserve space: 24px top padding + 24px bottom + ~70px for nav + 24px ghost peek
      const reserved = 142;
      const ideal = Math.min(380, Math.max(260, available - reserved));
      setCardHeight(ideal);
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const t = setInterval(() => advance(1), 4000);
    return () => clearInterval(t);
  }, [active]);

  const advance = (dir) => {
    setVisible(false);
    setTimeout(() => {
      setActive((p) => (p + dir + total) % total);
      setVisible(true);
    }, 320);
  };

  const goTo = (i) => {
    if (i === active) return;
    setVisible(false);
    setTimeout(() => { setActive(i); setVisible(true); }, 320);
  };

  const slide = SLIDES[active];

  return (
    <div ref={rootRef} style={s.root}>
      {/* Ambient glows */}
      <div style={s.glow1} />
      <div style={s.glow2} />

      {/* ── Stack illusion: 3 absolutely-positioned card shells ── */}
      <div style={{ ...s.stackWrapper, height: cardHeight + 20 }}>

        {/* Ghost card 3 — furthest back */}
        <div style={{ ...s.ghost3, height: cardHeight }} />

        {/* Ghost card 2 — middle */}
        <div style={{ ...s.ghost2, height: cardHeight }} />

        {/* Front card — real content */}
        <div style={{ ...s.frontCard, height: cardHeight }}>
          <div style={{ ...s.content,
            padding: cardHeight < 320 ? "16px 18px 14px" : "26px 24px 22px",
            gap: cardHeight < 320 ? 10 : 18,
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(6px)",
            transition: "opacity 0.32s ease, transform 0.32s ease"
          }}>

            <div style={s.headerRow}>
              {slide.icon}
              <span style={s.tag}>{slide.tag}</span>
            </div>

            <div style={s.previewBox}>
              {slide.preview}
            </div>

            <div style={s.textBlock}>
              <p style={{ ...s.title, fontSize: cardHeight < 320 ? 15 : 18 }}>{slide.title}</p>
              <p style={{ ...s.desc, fontSize: cardHeight < 320 ? 11 : 13, display: cardHeight < 300 ? "none" : "block" }}>{slide.desc}</p>
            </div>

          </div>
        </div>

      </div>

      {/* Nav */}
      <div style={s.nav}>
        <button style={s.navBtn} onClick={() => advance(-1)}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M9 2L4 7l5 5" stroke="rgba(255,255,255,0.8)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <div style={s.dots}>
          {SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              style={{ ...s.dot, width: i === active ? 24 : 8, background: i === active ? "#fff" : "rgba(255,255,255,0.28)" }}
            />
          ))}
        </div>
        <button style={s.navBtn} onClick={() => advance(1)}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M5 2l5 5-5 5" stroke="rgba(255,255,255,0.8)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}

const cardBase = {
  position: "absolute",
  left: 0,
  right: 0,
  borderRadius: 22,
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
};

const s = {
  root: {
    position: "relative",
    width: "100%",
    margin: "16px",
    height: "calc(100% - 32px)",
    borderRadius: 30,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px 0",
    boxSizing: "border-box",
    background: "linear-gradient(160deg, #1a1050 0%, #2d1f8a 45%, #4e3bbf 100%)",
  },

  glow1: {
    position: "absolute",
    top: "-5%",
    right: "-5%",
    width: "55%",
    height: "55%",
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(168, 139, 250, 0.15) 0%, transparent 65%)",
    pointerEvents: "none",
  },
  glow2: {
    position: "absolute",
    bottom: "10%",
    left: "0%",
    width: "45%",
    height: "45%",
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(99,102,241,0.18) 0%, transparent 70%)",
    pointerEvents: "none",
  },

  // ── Stack wrapper: fixed height = card height + ghost peek space ──
  stackWrapper: {
    position: "relative",
    width: "78%",
    height: 380 + 20,   // 380px card + 20px for ghost peek below
    zIndex: 1,
  },

  // Ghost card 3 — widest shrink, lowest, most transparent
  ghost3: {
    ...cardBase,
    top: 0,
    left: "7%",
    right: "7%",
    height: 380,
    background: "rgba(255,255,255,0.045)",
    border: "1px solid rgba(255,255,255,0.08)",
    transform: "translateY(20px)",
    opacity: 0.5,
    zIndex: 1,
    boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
  },

  // Ghost card 2 — medium, closer
  ghost2: {
    ...cardBase,
    top: 0,
    left: "3.5%",
    right: "3.5%",
    height: 380,
    background: "rgba(255,255,255,0.07)",
    border: "1px solid rgba(255,255,255,0.11)",
    transform: "translateY(10px)",
    opacity: 0.7,
    zIndex: 2,
    boxShadow: "0 6px 24px rgba(0,0,0,0.25)",
  },

  // Front card — full opacity, real content
  frontCard: {
    position: "relative",
    zIndex: 3,
    borderRadius: 22,
    background: "rgba(255,255,255,0.11)",
    border: "1px solid rgba(255,255,255,0.16)",
    backdropFilter: "blur(24px)",
    WebkitBackdropFilter: "blur(24px)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.4), 0 1px 0 rgba(255,255,255,0.14) inset",
    height: 380,
    display: "flex",
    flexDirection: "column",
  },

  content: {
    padding: "26px 24px 22px",
    display: "flex",
    flexDirection: "column",
    gap: 18,
    flex: 1,
    minHeight: 0,
  },

  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  tag: {
    fontSize: 11,
    fontWeight: 600,
    color: "rgba(255,255,255,0.85)",
    background: "rgba(168,139,250,0.18)",
    border: "1px solid rgba(168,139,250,0.3)",
    padding: "4px 12px",
    borderRadius: 20,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
  },

  previewBox: {
    borderRadius: 14,
    background: "rgba(0,0,0,0.18)",
    border: "1px solid rgba(255,255,255,0.07)",
    padding: "16px",
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "clip",
  },







  textBlock: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    paddingBottom: 2,
  },

  title: {
    margin: 0,
    fontSize: 18,
    fontWeight: 700,
    color: "#fff",
    lineHeight: 1.3,
  },

  desc: {
    margin: 0,
    fontSize: 13,
    color: "rgba(255,255,255,0.52)",
    lineHeight: 1.5,
  },

  nav: {
    position: "relative",
    zIndex: 10,
    marginTop: 20,
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexShrink: 0,
  },

  navBtn: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.09)",
    border: "1px solid rgba(255,255,255,0.16)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },

  dots: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },

  dot: {
    height: 8,
    borderRadius: 4,
    border: "none",
    cursor: "pointer",
    padding: 0,
    transition: "width 0.3s ease, background 0.3s ease",
  },
};